// Service Lane Workflow definitions (PROMPT 9)
// Each lane has: key, label, icon (lucide name), accent, pipeline stages, and a field schema.
// Field types: text | textarea | number | currency | date | select | switch | staff

const SELL_TIMELINE = [
  { value: "immediately", label: "Need to sell immediately" },
  { value: "7_days", label: "7 days" },
  { value: "14_days", label: "14 days" },
  { value: "30_days", label: "30 days" },
  { value: "60_days", label: "60 days" },
  { value: "90_days", label: "90 days" },
  { value: "flexible", label: "Flexible" },
  { value: "exploring", label: "Just exploring" },
];

export const LANE_WORKFLOWS = [
  {
    key: "sell_home",
    label: "Sell the Home",
    icon: "Home",
    accent: "#9C1E22",
    pipeline: [
      { value: "new_property_lead", label: "New property lead" },
      { value: "property_info_requested", label: "Property info requested" },
      { value: "photos_documents_requested", label: "Photos / documents requested" },
      { value: "property_reviewed", label: "Property reviewed" },
      { value: "seller_consultation_completed", label: "Seller consultation completed" },
      { value: "offer_strategy_selected", label: "Offer strategy selected" },
      { value: "investor_purchaser_interest", label: "Investor / purchaser interest" },
      { value: "offer_submitted", label: "Offer submitted" },
      { value: "agreement_pending", label: "Agreement pending" },
      { value: "under_contract", label: "Under contract" },
      { value: "closing_scheduled", label: "Closing scheduled" },
      { value: "closed", label: "Closed" },
      { value: "lost", label: "Lost / not moving forward" },
    ],
    fields: [
      { key: "property_address", label: "Property address", type: "text" },
      { key: "owner_name", label: "Owner name", type: "text" },
      { key: "occupancy_status", label: "Occupancy status", type: "select", options: ["Owner-occupied", "Vacant", "Tenant-occupied", "Family member living there"] },
      { key: "property_condition", label: "Property condition", type: "select", options: ["Excellent", "Good", "Fair", "Poor", "Needs major work"] },
      { key: "repairs_needed", label: "Repairs needed", type: "textarea" },
      { key: "mortgage_status", label: "Mortgage status", type: "select", options: ["Paid off", "Mortgage current", "Behind on payments", "In foreclosure", "Reverse mortgage", "Not sure"] },
      { key: "taxes_owed", label: "Taxes owed", type: "currency" },
      { key: "estimated_value", label: "Estimated value", type: "currency" },
      { key: "asking_expectation", label: "Asking expectation", type: "currency" },
      { key: "timeline_to_sell", label: "Timeline to sell", type: "select", options: SELL_TIMELINE },
      { key: "seller_motivation", label: "Seller motivation", type: "textarea" },
      { key: "decision_maker", label: "Decision maker", type: "text" },
      { key: "assigned_manager", label: "Assigned real estate / wholesaling manager", type: "staff" },
    ],
  },
  {
    key: "senior_placement",
    label: "Senior Living Placement",
    icon: "Building2",
    accent: "#1C4358",
    pipeline: [
      { value: "placement_need_identified", label: "Placement need identified" },
      { value: "preferences_collected", label: "Preferences collected" },
      { value: "facility_options_researched", label: "Facility options researched" },
      { value: "facilities_contacted", label: "Facilities contacted" },
      { value: "tours_scheduled", label: "Tours scheduled" },
      { value: "family_reviewing", label: "Family reviewing" },
      { value: "facility_selected", label: "Facility selected" },
      { value: "move_in_planned", label: "Move-in planned" },
      { value: "placement_completed", label: "Placement completed" },
      { value: "revenue_pending", label: "Revenue pending" },
      { value: "revenue_received", label: "Revenue received" },
    ],
    fields: [
      { key: "preferred_location", label: "Preferred location", type: "text" },
      { key: "distance_from_family", label: "Distance from family", type: "text" },
      { key: "budget_range", label: "Budget range", type: "text" },
      { key: "care_type", label: "Care type being considered", type: "select", options: ["Independent living", "Assisted living", "Memory care", "Skilled nursing", "In-home care", "Not sure"] },
      { key: "timeline", label: "Timeline", type: "select", options: SELL_TIMELINE },
      { key: "facility_options", label: "Facility options", type: "textarea" },
      { key: "tours_scheduled", label: "Tours scheduled", type: "textarea" },
      { key: "family_reviewing_options", label: "Family reviewing options", type: "switch" },
      { key: "facility_selected", label: "Facility selected", type: "text" },
      { key: "move_in_date", label: "Move-in date", type: "date" },
      { key: "referral_revenue_status", label: "Referral revenue status", type: "select", options: ["Not applicable", "Pending", "Invoiced", "Received"] },
    ],
  },
  {
    key: "home_safety",
    label: "Keep Your Loved One Home",
    icon: "ShieldCheck",
    accent: "#256B4B",
    pipeline: [
      { value: "safety_concern_identified", label: "Safety concern identified" },
      { value: "home_assessment_requested", label: "Home assessment requested" },
      { value: "assessment_completed", label: "Assessment completed" },
      { value: "needs_listed", label: "Repairs / accessibility needs listed" },
      { value: "contractor_assigned", label: "Contractor / vendor assigned" },
      { value: "estimate_requested", label: "Estimate requested" },
      { value: "funding_reviewed", label: "Funding reviewed" },
      { value: "family_approval_pending", label: "Family approval pending" },
      { value: "work_scheduled", label: "Work scheduled" },
      { value: "work_completed", label: "Work completed" },
      { value: "follow_up_completed", label: "Follow-up completed" },
    ],
    fields: [
      { key: "current_home_type", label: "Current home type", type: "select", options: ["Single-family", "Apartment", "Condo", "Mobile home", "Multi-level", "Other"] },
      { key: "lives_alone", label: "Lives alone", type: "switch" },
      { key: "stairs", label: "Stairs", type: "select", options: ["No stairs", "A few steps", "Full staircase", "Multiple staircases"] },
      { key: "bathroom_safety", label: "Bathroom safety", type: "textarea" },
      { key: "entryway_safety", label: "Entryway safety", type: "textarea" },
      { key: "lighting", label: "Lighting", type: "textarea" },
      { key: "bedroom_access", label: "Bedroom access", type: "textarea" },
      { key: "kitchen_access", label: "Kitchen access", type: "textarea" },
      { key: "emergency_contact_system", label: "Emergency contact system", type: "select", options: ["None", "Medical alert device", "Phone-based", "Other"] },
      { key: "repairs_needed", label: "Repairs needed", type: "textarea" },
      { key: "accessibility_needs", label: "Accessibility needs", type: "textarea" },
      { key: "contractor_needed", label: "Contractor needed", type: "switch" },
      { key: "funding_options", label: "Funding options", type: "textarea" },
    ],
  },
  {
    key: "move_into_my_home",
    label: "Move Your Loved One Into My Home",
    icon: "HousePlus",
    accent: "#1C4358",
    pipeline: [
      { value: "family_considering", label: "Family considering move-in" },
      { value: "suitability_intake_completed", label: "Home suitability intake completed" },
      { value: "home_assessment_scheduled", label: "Home assessment scheduled" },
      { value: "needs_identified", label: "Renovation / accessibility needs identified" },
      { value: "contractor_assigned", label: "Contractor / vendor assigned" },
      { value: "funding_reviewed", label: "Funding options reviewed" },
      { value: "current_home_sale_reviewed", label: "Current home sale reviewed" },
      { value: "care_support_reviewed", label: "Care support reviewed" },
      { value: "action_plan_created", label: "Family action plan created" },
      { value: "work_completed", label: "Work completed" },
      { value: "move_in_completed", label: "Move-in completed" },
    ],
    fields: [
      { key: "whose_home", label: "Whose home would your loved one move into?", type: "text" },
      { key: "spare_bedroom", label: "Is there a spare bedroom?", type: "switch" },
      { key: "stairs", label: "Are there stairs?", type: "switch" },
      { key: "bathroom_main_level", label: "Bathroom on main level?", type: "switch" },
      { key: "renovations_needed", label: "Renovations needed?", type: "switch" },
      { key: "caregiving_support_needed", label: "Caregiving support needed?", type: "switch" },
      { key: "financial_plan_needed", label: "Financial plan needed?", type: "switch" },
      { key: "permits_contractors_needed", label: "Permits / contractors needed?", type: "switch" },
      { key: "current_home_sale_needed", label: "Current home sale needed?", type: "switch" },
      { key: "accessibility_modifications_needed", label: "Accessibility modifications needed?", type: "switch" },
    ],
  },
  {
    key: "downsizing",
    label: "Downsizing / Estate / Belongings",
    icon: "PackageOpen",
    accent: "#9A6516",
    pipeline: [
      { value: "downsizing_need_identified", label: "Downsizing need identified" },
      { value: "inventory_intake_started", label: "Inventory intake started" },
      { value: "priorities_collected", label: "Family priorities collected" },
      { value: "vendor_assigned", label: "Vendor assigned" },
      { value: "auction_estate_reviewed", label: "Auction / estate option reviewed" },
      { value: "moving_storage_plan_created", label: "Moving / storage plan created" },
      { value: "cleanout_scheduled", label: "Cleanout scheduled" },
      { value: "belongings_processed", label: "Belongings processed" },
      { value: "home_ready", label: "Home ready" },
      { value: "completed", label: "Completed" },
    ],
    fields: [
      { key: "home_size", label: "Home size", type: "select", options: ["Studio", "1-2 bedrooms", "3 bedrooms", "4+ bedrooms", "Large estate"] },
      { key: "belongings_amount", label: "Amount of belongings", type: "select", options: ["Minimal", "Moderate", "Significant", "Full household", "Decades of belongings"] },
      { key: "urgency", label: "Urgency", type: "select", options: SELL_TIMELINE },
      { key: "family_available_to_help", label: "Family available to help?", type: "switch" },
      { key: "items_to_keep", label: "Items to keep", type: "textarea" },
      { key: "items_to_sell", label: "Items to sell", type: "textarea" },
      { key: "items_to_donate", label: "Items to donate", type: "textarea" },
      { key: "items_to_discard", label: "Items to discard", type: "textarea" },
      { key: "auction_needed", label: "Auction needed", type: "switch" },
      { key: "estate_sale_needed", label: "Estate sale needed", type: "switch" },
      { key: "moving_company_needed", label: "Moving company needed", type: "switch" },
      { key: "storage_needed", label: "Storage needed", type: "switch" },
      { key: "cleanout_vendor_needed", label: "Cleanout vendor needed", type: "switch" },
    ],
  },
  {
    key: "overwhelmed",
    label: "Not Sure / Overwhelmed",
    icon: "HeartHandshake",
    accent: "#9BB9CE",
    convertible: true,
    pipeline: [
      { value: "intake_started", label: "Intake started" },
      { value: "intake_completed", label: "Intake completed" },
      { value: "routed", label: "Routed to service lanes" },
    ],
    fields: [
      { key: "what_happened", label: "What happened that made you reach out today?", type: "textarea" },
      { key: "biggest_pressure", label: "Biggest pressure", type: "textarea" },
      { key: "deadline", label: "Deadline", type: "text" },
      { key: "loved_one_safe_today", label: "Is your loved one safe today?", type: "switch" },
      { key: "home_involved", label: "Is a home involved?", type: "switch" },
      { key: "money_care_concern", label: "Is money / care cost a concern?", type: "switch" },
      { key: "family_in_agreement", label: "Is the family in agreement?", type: "switch" },
      { key: "decision_maker", label: "Who is the decision maker?", type: "text" },
      { key: "calmer_this_week", label: "What would make things feel calmer this week?", type: "textarea" },
    ],
  },
];

export const LANE_BY_KEY = LANE_WORKFLOWS.reduce((acc, l) => { acc[l.key] = l; return acc; }, {});

// Lanes the Overwhelmed intake can be converted into (the 5 concrete lanes)
export const CONVERTIBLE_TARGETS = LANE_WORKFLOWS.filter((l) => l.key !== "overwhelmed");

export const laneStageLabel = (laneKey, stageValue) => {
  const lane = LANE_BY_KEY[laneKey];
  if (!lane) return stageValue || "";
  return lane.pipeline.find((s) => s.value === stageValue)?.label || stageValue || "Not started";
};

export const firstStage = (laneKey) => LANE_BY_KEY[laneKey]?.pipeline?.[0]?.value || null;
